/**
 * Wallpaper Builder — MOOUI
 * @author Fabiano Omura — Mooui Labs
 * @copyright 2026 Mooui Labs. All rights reserved.
 */

class WallpaperCustomizer {
  constructor() {
    this.state = {
      stripeWidth: 'larga',   // ID da largura selecionada (padrão: LARGA)
      color1: 'offwhite',     // ID da cor 1 (padrão: OFF WHITE)
      color2: 'creme',        // ID da cor 2 (padrão: CREME)
      quantity: 1,            // Quantidade
      acceptedTerms: false,   // Checkbox de consentimento
      sku: null,              // SKU gerado
      price: window.WALLPAPER_CONFIG.basePrice
    };

    this.config = window.WALLPAPER_CONFIG;
    this.setupDone = false;  // Flag para evitar setup duplicado
    this.isAddingToCart = false;  // Flag para evitar múltiplos submissions simultâneos
    this.init();
  }

  /**
   * Inicializa o customizador
   */
  init() {

    // Aguarda DOM estar pronto
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  /**
   * Configura event listeners
   */
  setup() {
    // Evita setup duplicado
    if (this.setupDone) {
      return;
    }


    // Verifica se os elementos necessários existem no DOM
    const color1Grid = document.getElementById('color1Grid');
    const color2Grid = document.getElementById('color2Grid');

    if (!color1Grid || !color2Grid) {
      console.error('❌ Elementos DOM não encontrados! color1Grid:', color1Grid, 'color2Grid:', color2Grid);
      // Tenta novamente após um pequeno delay
      setTimeout(() => this.setup(), 100);
      return;
    }

    // Sticky preview — 3 estados: topo / fixo / fundo
    this.setupStickyPreview();

    // Primeiro renderiza os botões de cor
    this.renderColorButtons();

    // Botões de largura de faixa - usar delegação
    const widthContainer = document.querySelector('.stripe-width-options');
    if (widthContainer) {
      widthContainer.addEventListener('click', (e) => {
        if (e.target.classList.contains('stripe-width-btn')) {
          this.selectStripeWidth(e.target.dataset.width);
        }
      });
    } else {
      console.error('❌ stripe-width-options não encontrado');
    }

    // Delegação para botões de cor 1
    if (color1Grid) {
      color1Grid.addEventListener('click', (e) => {
        const btn = e.target.closest('.color1-btn');
        if (btn && btn.dataset.colorId) {
          this.selectColor1(btn.dataset.colorId);
        }
      });
    }

    // Delegação para botões de cor 2
    if (color2Grid) {
      color2Grid.addEventListener('click', (e) => {
        const btn = e.target.closest('.color2-btn');
        if (btn && btn.dataset.colorId) {
          this.selectColor2(btn.dataset.colorId);
        }
      });
    }

    // Checkbox de consentimento
    const termsCheckbox = document.getElementById('termsCheckbox');
    if (termsCheckbox) {
      termsCheckbox.addEventListener('change', (e) => {
        this.state.acceptedTerms = e.target.checked;
        this.updateAddToCartButton();
      });
    }

    // Botão de adicionar ao carrinho (com proteção contra cliques múltiplos)
    const addToCartBtn = document.getElementById('addToCartBtn');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => {
        if (!this.isAddingToCart) {
          this.addToCart();
        }
      });
    }

    // Botão recomeçar
    const startOverBtn = document.getElementById('btnStartOver');
    if (startOverBtn) {
      startOverBtn.addEventListener('click', () => this.startOver());
    }

    // Botões de quantidade — usar delegação para garantir que funciona mesmo se adicionados depois
    document.addEventListener('click', (e) => {
      if (e.target.id === 'quantityMinus') {
        this.decreaseQuantity();
      } else if (e.target.id === 'quantityPlus') {
        this.increaseQuantity();
      }
    });

    document.addEventListener('change', (e) => {
      if (e.target.id === 'quantityInput') {
        this.setQuantity(parseInt(e.target.value));
      }
    });

    // Botão adicionar ao carrinho inline (com proteção contra cliques múltiplos)
    const addToCartBtnInline = document.getElementById('addToCartBtnInline');
    if (addToCartBtnInline) {
      addToCartBtnInline.addEventListener('click', () => {
        if (!this.isAddingToCart) {
          this.addToCart();
        }
      });
    }

    // Acordeão
    document.querySelectorAll('.accordion-header').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const header = e.currentTarget;
        const accordionId = header.dataset.accordion;
        const content = document.getElementById(`accordion-${accordionId}`);
        header.classList.toggle('active');
        content.classList.toggle('active');
      });
    });

    // Re-renderiza preview ao redimensionar (mobile ↔ desktop, rotação)
    window.addEventListener('resize', () => this.updatePreview(), { passive: true });

    // Estado inicial: marcar botões e renderizar preview de uma vez (sem setTimeout)
    const defaultWidth = document.querySelector('[data-width="larga"]');
    if (defaultWidth) defaultWidth.classList.add('active');

    const defaultColor1 = document.querySelector('.color1-btn[data-color-id="offwhite"]');
    if (defaultColor1) defaultColor1.classList.add('active');

    const defaultColor2 = document.querySelector('.color2-btn[data-color-id="creme"]');
    if (defaultColor2) defaultColor2.classList.add('active');

    // Desabilita cor igual no grid 2
    this.updateColor2Availability('offwhite');

    // Mostra seções sem scroll/animação
    const c1s = document.getElementById('color1Section');
    const c2s = document.getElementById('color2Section');
    if (c1s) c1s.style.display = 'block';
    if (c2s) c2s.style.display = 'block';

    // Renderiza preview e SKU
    this.updatePreview();
    this.generateSKU();
    this.showStartOverButton();
    this.showQuantityBuySection();
    this.updateAddToCartButton();
    this.updateSummary();

    // Marca setup como completo para evitar duplicatas
    this.setupDone = true;
  }

  setupStickyPreview() {
    const preview = document.querySelector('.wallpaper-preview-container');
    const stickyHeader = document.querySelector('.wallpaper-sticky-header');
    const layout  = document.querySelector('.wallpaper-main-layout');

    if (!preview || !layout || !stickyHeader) return;

    const MOBILE_BP = 1024;
    let rafId = null;
    let lastWasFixed = false; // Guard para evitar piscar

    // Criar sentinel element para mobile (ocupa espaço quando header fica fixed)
    const sentinel = document.createElement('div');
    sentinel.className = 'wallpaper-sticky-sentinel';
    sentinel.style.height = stickyHeader.offsetHeight + 'px';
    sentinel.style.display = 'none';
    stickyHeader.parentNode.insertBefore(sentinel, stickyHeader.nextSibling);

    const updateDesktop = () => {
      const rect    = layout.getBoundingClientRect();
      const inZone  = rect.top <= 0 && rect.bottom >= window.innerHeight;
      const pastEnd = rect.bottom < window.innerHeight;
      preview.classList.toggle('is-fixed',  inZone);
      preview.classList.toggle('is-bottom', !inZone && pastEnd);
    };

    const updateMobile = () => {
      // Se header já está fixed, usar sentinel para detectar quando voltar
      if (lastWasFixed) {
        const sentinelRect = sentinel.getBoundingClientRect();
        if (sentinelRect.top > 0) {
          // Sentinel visível = header voltou = remover fixed
          stickyHeader.classList.remove('is-mobile-fixed');
          sentinel.style.display = 'none';
          lastWasFixed = false;
        }
      } else {
        // Se não está fixed, ver se header saiu da tela
        const headerRect = stickyHeader.getBoundingClientRect();
        if (headerRect.top < 0) {
          // Header saiu = fixar
          stickyHeader.classList.add('is-mobile-fixed');
          sentinel.style.display = 'block';
          sentinel.style.height = stickyHeader.offsetHeight + 'px';
          lastWasFixed = true;
        }
      }
    };

    // Scroll listener único
    window.addEventListener('scroll', () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        if (window.innerWidth > MOBILE_BP) {
          updateDesktop();
        } else {
          updateMobile();
        }
        rafId = null;
      });
    }, { passive: true });

    // Accordion: re-avalia após animação
    document.querySelectorAll('.accordion-header').forEach(h =>
      h.addEventListener('click', () => {
        setTimeout(() => {
          if (window.innerWidth > MOBILE_BP) {
            updateDesktop();
          } else {
            updateMobile();
          }
        }, 350);
      })
    );

    // ResizeObserver
    if (window.ResizeObserver) {
      new ResizeObserver(() => {
        if (window.innerWidth > MOBILE_BP) {
          updateDesktop();
        } else {
          updateMobile();
          sentinel.style.height = stickyHeader.offsetHeight + 'px';
        }
      }).observe(layout);
    }
  }

  /**
   * Renderiza os botões de cor dinamicamente
   */
  renderColorButtons() {

    // Se config não estiver disponível, tenta usar window.WALLPAPER_CONFIG
    const config = this.config || window.WALLPAPER_CONFIG;

    // Renderiza botões de Cor 1
    const color1Grid = document.getElementById('color1Grid');

    const makeColorBtn = (extraClass) => (color) => {
      const btn = document.createElement('button');
      btn.className = `${extraClass} color-btn`;
      btn.dataset.colorId = color.id;
      btn.style.backgroundColor = color.hex;
      btn.setAttribute('aria-label', color.name);

      const tooltip = document.createElement('div');
      tooltip.className = 'color-name-tooltip';
      tooltip.textContent = color.name;
      btn.appendChild(tooltip);

      return btn;
    };

    if (color1Grid && config && config.colors) {
      color1Grid.innerHTML = '';
      config.colors.forEach(color => {
        color1Grid.appendChild(
          makeColorBtn('color1-btn')(color)
        );
      });
    } else {
      console.error('❌ color1Grid ou config não disponível', { color1Grid, config });
    }

    const color2Grid = document.getElementById('color2Grid');

    if (color2Grid && config && config.colors) {
      color2Grid.innerHTML = '';
      config.colors.forEach(color => {
        color2Grid.appendChild(
          makeColorBtn('color2-btn')(color)
        );
      });
    } else {
      console.error('❌ color2Grid ou config não disponível', { color2Grid, config });
    }
  }

  /**
   * PASSO 1: Seleciona largura da faixa
   */
  selectStripeWidth(widthId) {

    this.state.stripeWidth = widthId;

    // Remove classe active de todos os botões
    document.querySelectorAll('.stripe-width-btn').forEach(btn => {
      btn.classList.remove('active');
    });

    // Adiciona classe active no botão clicado
    const selectedBtn = document.querySelector(`[data-width="${widthId}"]`);
    if (selectedBtn) {
      selectedBtn.classList.add('active');
    }

    // Mostra seção de cor 1
    this.showColor1Section();

    // Atualiza preview
    this.updatePreview();

    // Mostra botão recomeçar
    this.showStartOverButton();
  }

  /**
   * PASSO 2: Seleciona Cor 1 (cor base)
   */
  selectColor1(colorId) {

    this.state.color1 = colorId;

    // Remove classe active de todos os botões de cor 1
    document.querySelectorAll('.color1-btn').forEach(btn => {
      btn.classList.remove('active');
    });

    // Adiciona classe active no botão clicado
    const selectedBtn = document.querySelector(`.color1-btn[data-color-id="${colorId}"]`);
    if (selectedBtn) {
      selectedBtn.classList.add('active');
    }

    // Se Cor 2 já estava selecionada com a mesma cor, limpa
    if (this.state.color2 === colorId) {
      this.state.color2 = null;
      document.querySelectorAll('.color2-btn').forEach(btn => btn.classList.remove('active'));
      const qSection = document.getElementById('quantityBuySection');
      if (qSection) { qSection.style.display = 'none'; qSection.classList.remove('show'); }
    }

    // Desabilita a mesma cor no grid de Cor 2
    this.updateColor2Availability(colorId);

    // Mostra seção de cor 2
    this.showColor2Section();

    // Atualiza preview
    this.updatePreview();
  }

  /**
   * Habilita/desabilita botões no grid de Cor 2 de acordo com a Cor 1 selecionada.
   * Passa null para reabilitar todas (ex: ao recomeçar).
   */
  updateColor2Availability(color1Id) {
    document.querySelectorAll('.color2-btn').forEach(btn => {
      const isSame = color1Id !== null && btn.dataset.colorId === color1Id;
      btn.disabled = isSame;
      btn.classList.toggle('unavailable', isSame);
    });
  }

  /**
   * PASSO 3: Seleciona Cor 2 (cor da listra)
   * Fluxo seguro: atualizar preview → estabilizar → gerar SKU → buscar produto → mostrar compra
   */
  selectColor2(colorId) {

    this.state.color2 = colorId;

    // Remove classe active de todos os botões de cor 2
    document.querySelectorAll('.color2-btn').forEach(btn => {
      btn.classList.remove('active');
    });

    // Adiciona classe active no botão clicado
    const selectedBtn = document.querySelector(`.color2-btn[data-color-id="${colorId}"]`);
    if (selectedBtn) {
      selectedBtn.classList.add('active');
    }

    // 1️⃣ Atualiza preview imediatamente
    this.updatePreview();

    // 2️⃣ Aguarda preview estabilizar (~100ms) antes de gerar SKU e buscar produto
    setTimeout(() => {

      // 3️⃣ Gera SKU
      this.generateSKU();

      // 4️⃣ Valida se produto existe antes de mostrar compra
      const product = this.config.getProduct(
        this.state.stripeWidth,
        this.state.color1,
        this.state.color2
      );

      if (product && product.variantId) {
        // 5️⃣ Só mostra seção de quantidade e compra se produto existe
        this.showQuantityBuySection();
        this.updateAddToCartButton();
      } else {
        const qSection = document.getElementById('quantityBuySection');
        if (qSection) {
          qSection.style.display = 'none';
          qSection.classList.remove('show');
        }
        // Mostra aviso ao usuário
        alert('Este produto ainda não está disponível para esta combinação. Por favor, escolha outras cores.');
      }

      // 6️⃣ Atualiza resumo
      this.updateSummary();
    }, 100);  // Pequeno delay para estabilizar
  }

  /**
   * Mostra seção de cor 1
   */
  showColor1Section() {
    const section = document.getElementById('color1Section');
    if (section) {
      section.style.display = 'block';
      if (this.setupDone) section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * Mostra seção de cor 2
   */
  showColor2Section() {
    const section = document.getElementById('color2Section');
    if (section) {
      section.style.display = 'block';
      if (this.setupDone) section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * Mostra seção de quantidade e comprar
   */
  showQuantityBuySection() {
    const section = document.getElementById('quantityBuySection');
    if (section) {
      section.classList.add('show');
      section.style.display = 'block';
      if (this.setupDone) section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * Mostra seção de consentimento
   */
  showConsentSection() {
    const section = document.getElementById('consentSection');
    if (section) {
      section.style.display = 'block';
      if (this.setupDone) section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * Mostra botão recomeçar
   */
  showStartOverButton() {
    const container = document.getElementById('startOverContainer');
    if (container) {
      container.style.display = 'block';
    }
  }

  /**
   * Aumenta quantidade
   */
  increaseQuantity() {
    const input = document.getElementById('quantityInput');
    const currentValue = parseInt(input.value) || 1;
    if (currentValue < 99) {
      this.state.quantity = currentValue + 1;
      input.value = this.state.quantity;
    }
  }

  /**
   * Diminui quantidade
   */
  decreaseQuantity() {
    const input = document.getElementById('quantityInput');
    const currentValue = parseInt(input.value) || 1;
    if (currentValue > 1) {
      this.state.quantity = currentValue - 1;
      input.value = this.state.quantity;
    }
  }

  /**
   * Define quantidade
   */
  setQuantity(value) {
    const input = document.getElementById('quantityInput');
    if (value < 1) {
      this.state.quantity = 1;
      input.value = 1;
    } else if (value > 99) {
      this.state.quantity = 99;
      input.value = 99;
    } else {
      this.state.quantity = value;
    }
  }

  /**
   * Atualiza preview visual das listras usando CSS repeating-linear-gradient.
   * Zero requisições de rede — renderização instantânea pelo browser.
   */
  updatePreview() {
    if (!this.state.stripeWidth) return;

    const stripeEl = document.getElementById('stripeColor1');
    if (!stripeEl) return;

    const color1 = this.state.color1
      ? this.config.colors.find(c => c.id === this.state.color1)?.hex
      : null;

    if (!color1) {
      stripeEl.style.background = '';
      return;
    }

    const widthConfig = this.config.stripeWidths.find(w => w.id === this.state.stripeWidth);
    const totalStripes = widthConfig ? widthConfig.totalStripes : 10;
    const pct = 100 / totalStripes;
    const c2 = this.state.color2
      ? (this.config.colors.find(c => c.id === this.state.color2)?.hex || '#ffffff')
      : '#ffffff';

    const grad = `repeating-linear-gradient(to right, ${color1} 0, ${color1} ${pct}%, ${c2} ${pct}%, ${c2} ${pct * 2}%)`;
    const stripeW = Math.min(Math.round(stripeEl.offsetHeight * 5000 / 6925), stripeEl.offsetWidth);
    stripeEl.style.background = grad;
    stripeEl.style.backgroundSize = `${stripeW}px 100%`;
    stripeEl.style.backgroundPosition = 'center';
    stripeEl.style.backgroundRepeat = 'no-repeat';
  }

  /**
   * Cria listras irregulares (tamanhos variados)
   */
  createIrregularStripes(stripesElement, color1Hex, color2Hex) {
    // Para irregular, criamos um padrão VERTICAL com tamanhos variados
    const irregularPattern = `
      repeating-linear-gradient(
        to right,
        ${color1Hex} 0px,
        ${color1Hex} 15px,
        ${color2Hex} 15px,
        ${color2Hex} 35px,
        ${color1Hex} 35px,
        ${color1Hex} 75px,
        ${color2Hex} 75px,
        ${color2Hex} 95px,
        ${color1Hex} 95px,
        ${color1Hex} 130px,
        ${color2Hex} 130px,
        ${color2Hex} 160px
      )
    `;

    stripesElement.style.background = irregularPattern;
  }

  /**
   * Cria listras onduladas (padrão ondulado)
   */
  createWavyStripes(stripesElement, color1Hex, color2Hex) {
    // SVG com padrão ondulado vertical
    const svgPattern = `
      <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100%" viewBox="0 0 100 500" preserveAspectRatio="none">
        <defs>
          <pattern id="wavyPattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
            <path d="M 0 0 Q 25 25 50 0 T 100 0 V 100 Q 75 75 50 100 T 0 100 Z" fill="${color1Hex}"/>
            <path d="M 50 0 Q 75 25 100 0 V 100 Q 75 75 50 100 Z" fill="${color2Hex}"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#wavyPattern)"/>
      </svg>
    `;

    const svgDataUrl = 'data:image/svg+xml;base64,' + btoa(svgPattern);
    stripesElement.style.background = `url("${svgDataUrl}") repeat`;
    stripesElement.style.backgroundSize = '100px auto';
  }

  /**
   * Gera SKU baseado nas seleções
   */
  generateSKU() {
    if (!this.state.stripeWidth || !this.state.color1 || !this.state.color2) {
      return null;
    }

    this.state.sku = this.config.generateSKU(
      this.state.stripeWidth,
      this.state.color1,
      this.state.color2
    );


    // Debug: verifica se SKU existe em WALLPAPER_PRODUCTS
    const skuExists = this.config.WALLPAPER_PRODUCTS.hasOwnProperty(this.state.sku);

    // Atualiza na UI
    const skuElement = document.getElementById('generatedSKU');
    if (skuElement) {
      skuElement.textContent = this.state.sku;
    }

    return this.state.sku;
  }

  /**
   * Atualiza resumo no rodapé
   */
  updateSummary() {
    // Atualiza textos do resumo
    const widthName = this.config.stripeWidths.find(
      w => w.id === this.state.stripeWidth
    )?.name || '-';

    const color1Name = this.config.colors.find(
      c => c.id === this.state.color1
    )?.name || '-';

    const color2Name = this.config.colors.find(
      c => c.id === this.state.color2
    )?.name || '-';

    // Atualiza elementos na UI
    const widthSummary = document.getElementById('summaryWidth');
    const color1Summary = document.getElementById('summaryColor1');
    const color2Summary = document.getElementById('summaryColor2');
    const priceSummary = document.getElementById('summaryPrice');

    if (widthSummary) widthSummary.textContent = widthName;
    if (color1Summary) color1Summary.textContent = color1Name;
    if (color2Summary) color2Summary.textContent = color2Name;
    if (priceSummary) {
      const priceFormatted = (this.state.price / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      });
      priceSummary.textContent = priceFormatted;
    }
  }

  /**
   * Atualiza estado do botão adicionar ao carrinho
   */
  updateAddToCartButton() {
    const btn = document.getElementById('addToCartBtn');
    const btnInline = document.getElementById('addToCartBtnInline');

    // Removido requisito de aceitar termos
    const isComplete = this.state.stripeWidth &&
                       this.state.color1 &&
                       this.state.color2;

    // Atualiza botão do rodapé (se existir)
    if (btn) {
      if (isComplete) {
        btn.disabled = false;
        btn.classList.remove('disabled');
        btn.textContent = 'Comprar';
      } else {
        btn.disabled = true;
        btn.classList.add('disabled');

        btn.textContent = 'Complete a seleção';
      }
    }

    // Atualiza botão inline
    if (btnInline) {
      if (isComplete) {
        btnInline.disabled = false;
        btnInline.classList.remove('disabled');
        btnInline.textContent = 'Comprar';
      } else {
        btnInline.disabled = true;
        btnInline.classList.add('disabled');

        btnInline.textContent = 'Complete a seleção';
      }
    }
  }

  /**
   * Adiciona ao carrinho via Shopify Cart API com retry inteligente para 429.
   *
   * Fluxo seguro com:
   * - Debounce (isAddingToCart flag)
   * - Respeita header Retry-After do Shopify
   * - Backoff exponencial: 2s, 4s, 8s, 16s
   * - Máximo 4 tentativas
   */
  async addToCart(retryCount = 0) {
    const MAX_RETRIES = 3; // Total: 4 tentativas (0, 1, 2, 3)

    // Guard contra cliques múltiplos — bloqueia apenas cliques do usuário, não retries internos
    if (retryCount === 0 && this.isAddingToCart) {
      return;
    }

    if (!this.state.stripeWidth || !this.state.color1 || !this.state.color2) {
      alert('Por favor, complete todas as seleções.');
      return;
    }

    const product = this.config.getProduct(
      this.state.stripeWidth, this.state.color1, this.state.color2
    );

    if (!product || !product.variantId) {
      alert('Este produto ainda não está disponível para venda. Em breve você poderá comprá-lo aqui!');
      return;
    }


    const btn = document.getElementById('addToCartBtn');
    const btnInline = document.getElementById('addToCartBtnInline');

    // Marca como em progresso ANTES de qualquer operação assíncrona (apenas na primeira tentativa)
    if (retryCount === 0) {
      this.isAddingToCart = true;
      if (btn) { btn.textContent = 'Adicionando...'; btn.disabled = true; }
      if (btnInline) { btnInline.textContent = 'Adicionando...'; btnInline.disabled = true; }
    }

    try {
      // ✅ SIMPLES: apenas variantId + quantidade
      // O SKU já está no título da variante no Shopify
      const formData = new URLSearchParams();
      formData.append('id', product.variantId);
      formData.append('quantity', this.state.quantity);


      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMsg = `Erro ${response.status}`;

        // Trata erro 429 (rate limit) — respeita header Retry-After do Shopify
        if (response.status === 429) {
          if (retryCount < MAX_RETRIES) {
            // 🔑 CRÍTICO: Respeita o header Retry-After do Shopify
            const retryAfterHeader = response.headers.get('Retry-After');
            const retryAfterSeconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : null;

            // Se Shopify retornou Retry-After, respeita. Senão usa backoff exponencial
            const waitMs = !isNaN(retryAfterSeconds) && retryAfterSeconds > 0
              ? retryAfterSeconds * 1000  // Respeita o que Shopify pediu
              : Math.min(1000 * Math.pow(2, retryCount + 1), 16000); // 2s, 4s, 8s, 16s


            await new Promise(resolve => setTimeout(resolve, waitMs));
            return this.addToCart(retryCount + 1);  // Retry recursivo
          } else {
            errorMsg = 'Servidor sobrecarregado (429). Aguarde alguns minutos e tente novamente.';
          }
        } else {
          // Captura resposta completa para diagnóstico (422, 404, etc.)
          try {
            const rawText = await response.text();
            console.error(`🔴 Resposta ${response.status}:`, rawText);
            try {
              const err = JSON.parse(rawText);
              errorMsg = err.description || err.message || errorMsg;
            } catch (e) {
              errorMsg = rawText || errorMsg;
            }
          } catch (e) {
            // fallback
          }
        }

        throw new Error(errorMsg);
      }

      this.showSuccessMessage();

      // Atualiza cart sidebar do tema e abre
      try {
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();

        // Atualiza contadores
        document.querySelectorAll('.cart-count, [data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
        });

        // Atualiza sidebar HTML via AJAX
        const sidebarRes = await fetch('/cart?view=ajax_side_cart');
        const sidebarHtml = await sidebarRes.text();
        const sidebarEl = document.querySelector('.previewCartCheckout') ||
                          document.querySelector('.halo-cart-sidebar .sidebar_content') ||
                          document.querySelector('#halo-cart-sidebar');
        if (sidebarEl) {
          sidebarEl.innerHTML = sidebarHtml;
        }

        // Abre o sidebar
        document.body.classList.add('cart-sidebar-show');
      } catch (e) {
        // Fallback: tenta método do tema
        if (typeof Shopify !== 'undefined' && Shopify.getCart && typeof halo !== 'undefined') {
          Shopify.getCart((c) => {
            halo.updateSidebarCart(c);
            document.body.classList.add('cart-sidebar-show');
          });
        }
      }

      // Reseta botão e flag
      setTimeout(() => {
        if (btn) { btn.textContent = 'Comprar'; btn.disabled = false; }
        if (btnInline) { btnInline.textContent = 'Comprar'; btnInline.disabled = false; }
        this.isAddingToCart = false;
      }, 1500);

    } catch (error) {
      console.error('❌ Erro ao adicionar ao carrinho:', error);

      // Feedback imediato ao usuário
      alert(error.message || 'Erro ao adicionar ao carrinho. Por favor, tente novamente.');

      // Reset imediato: botão volta ao normal, flag libera novo clique
      if (btn) { btn.textContent = 'Comprar'; btn.disabled = false; }
      if (btnInline) { btnInline.textContent = 'Comprar'; btnInline.disabled = false; }
      this.isAddingToCart = false;  // 🔑 Permite novo clique imediatamente em caso de erro
    }
  }

  /**
   * Atualiza contador do carrinho
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Erro ao atualizar contador do carrinho:', error);
    }
  }

  /**
   * Mostra mensagem de sucesso
   */
  showSuccessMessage() {
    const message = document.createElement('div');
    message.className = 'success-message';
    message.innerHTML = `
      <div class="success-content">
        <span class="success-text">Produto adicionado ao carrinho!</span>
      </div>
    `;
    message.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #7fb79a;
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-size: 14px;
    `;

    document.body.appendChild(message);

    setTimeout(() => {
      message.remove();
    }, 3000);
  }

  /**
   * Recomeça a composição
   */
  startOver() {

    // Reseta estado
    this.state = {
      stripeWidth: null,
      color1: null,
      color2: null,
      acceptedTerms: false,
      sku: null,
      price: window.WALLPAPER_CONFIG.basePrice
    };

    // Remove classes active de todos os botões e reabilita Cor 2
    document.querySelectorAll('.stripe-width-btn, .color1-btn, .color2-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    this.updateColor2Availability(null);

    // Desmarca checkbox
    const termsCheckbox = document.getElementById('termsCheckbox');
    if (termsCheckbox) {
      termsCheckbox.checked = false;
    }

    // Esconde seções
    const sectionsToHide = [
      'color1Section',
      'color2Section',
      'consentSection',
      'startOverContainer',
      'quantityBuySection'
    ];

    sectionsToHide.forEach(id => {
      const section = document.getElementById(id);
      if (section) {
        section.style.display = 'none';
      }
    });

    // Reseta preview — limpa gradient, mantém só o foreground transparente
    const stripeColor1 = document.getElementById('stripeColor1');
    if (stripeColor1) stripeColor1.style.cssText = '';

    // Reseta resumo
    this.updateSummary();

    // Reseta botão
    this.updateAddToCartButton();

    // Scroll para o topo
    window.scrollTo({ top: 0, behavior: 'smooth' });

  }
}

// Inicializa quando o documento estiver pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (window.wallpaperCustomizer) {
    } else {
      window.wallpaperCustomizer = new WallpaperCustomizer();
    }
  });
} else {
  if (window.wallpaperCustomizer) {
  } else {
    window.wallpaperCustomizer = new WallpaperCustomizer();
  }
}
